import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllUsers } from "../../redux/actions/user";
import { AiOutlineUser, AiOutlineUpload } from "react-icons/ai";
import { MdAdminPanelSettings, MdBlock, MdCheckCircle } from "react-icons/md";
import {
  FiUsers,
  FiSearch,
  FiTrash2,
  FiShield,
  FiShieldOff,
  FiUserPlus,
  FiSettings,
  FiDownload,
} from "react-icons/fi";
import { RxCross1 } from "react-icons/rx";
import { BiBlock } from "react-icons/bi";
import { SiMicrosoftexcel } from "react-icons/si";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import styles from "../../styles/styles";
import Loader from "../Layout/Loader";
import * as XLSX from "xlsx";
import { getAvatarUrl } from "../../utils/mediaUtils";

const AllUsers = () => {
  const dispatch = useDispatch();
  const {
    users,
    usersLoading,
    totalPages,
    totalUsersCount,
    bannedUsersCount,
    adminUsersCount,
    activeUsersCount,
  } = useSelector((state) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [unbanModalOpen, setUnbanModalOpen] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [createUserModalAnimating, setCreateUserModalAnimating] =
    useState(false);
  const [roleAssignModalOpen, setRoleAssignModalOpen] = useState(false);
  const [roleAssignModalAnimating, setRoleAssignModalAnimating] =
    useState(false);
  const [banModalAnimating, setBanModalAnimating] = useState(false);
  const [unbanModalAnimating, setUnbanModalAnimating] = useState(false);
  const [userId, setUserId] = useState("");
  const [banReason, setBanReason] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUserData, setNewUserData] = useState({
    name: "",
    email: "",
    password: "",
    role: "user",
    avatar: null,
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsModalAnimating, setDetailsModalAnimating] = useState(false);

  const openDetailsModal = (user) => {
    setSelectedUser(user);
    setDetailsModalOpen(true);
    setTimeout(() => setDetailsModalAnimating(true), 10);
  };

  const closeDetailsModal = () => {
    setDetailsModalAnimating(false);
    setTimeout(() => setDetailsModalOpen(false), 300);
  };

  useEffect(() => {
    dispatch(getAllUsers(currentPage, 12));
  }, [dispatch, currentPage]);

  // Export to Excel function with Orders data
  const exportToExcel = async () => {
    try {
      toast.info("Fetching all data... Please wait");

      // Fetch all users for export
      const usersResponse = await axios.get(
        `${server}/user/admin-all-users?limit=all`,
        { withCredentials: true }
      );
      const allUsers = usersResponse.data.users || [];

      if (allUsers.length === 0) {
        toast.error("No users to export");
        return;
      }

      // Fetch all orders from admin endpoint
      const ordersResponse = await axios.get(
        `${server}/order/admin-all-orders`,
        {
          withCredentials: true,
        }
      );
      const allOrders = ordersResponse.data.orders || [];

      // Create a map of user orders
      const userOrdersMap = {};
      allOrders.forEach((order) => {
        const userId = order.user?._id || order.user;
        if (userId) {
          if (!userOrdersMap[userId]) {
            userOrdersMap[userId] = [];
          }
          userOrdersMap[userId].push(order);
        }
      });

      // Prepare Users data for export
      const usersData = allUsers.map((user, index) => {
        const userOrders = userOrdersMap[user._id] || [];
        const totalSpent = userOrders.reduce(
          (sum, order) => sum + (order.totalPrice || 0),
          0
        );
        const completedOrders = userOrders.filter(
          (o) => o.status === "Delivered"
        ).length;
        const pendingOrders = userOrders.filter(
          (o) =>
            !["Delivered", "Cancelled", "Refund Success"].includes(o.status)
        ).length;
        const cancelledOrders = userOrders.filter(
          (o) => o.status === "Cancelled" || o.status === "Refund Success"
        ).length;

        return {
          "S.No": index + 1,
          "User ID": user._id,
          "Full Name": user.name,
          "Email Address": user.email,
          "Phone Number": user.phoneNumber || "N/A",
          Role: user.role === "user" ? "User" : user.role,
          Status: user.isBanned ? "Banned" : "Active",
          "Ban Reason": user.banReason || "N/A",
          "Email Verified": user.isEmailVerified ? "Yes" : "No",
          Address:
            user.addresses && user.addresses.length > 0
              ? `${user.addresses[0]?.address1 || ""}, ${
                  user.addresses[0]?.city || ""
                }, ${user.addresses[0]?.country || ""}`.replace(/^, |, $/g, "")
              : "N/A",
          "Total Orders": userOrders.length,
          "Completed Orders": completedOrders,
          "Pending Orders": pendingOrders,
          "Cancelled/Refunded": cancelledOrders,
          "Total Amount Spent": `$${totalSpent.toFixed(2)}`,
          "Account Created": user.createdAt
            ? new Date(user.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "N/A",
          "Last Updated": user.updatedAt
            ? new Date(user.updatedAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "N/A",
        };
      });

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Sheet 1: Users Overview
      const usersSheet = XLSX.utils.json_to_sheet(usersData);
      usersSheet["!cols"] = [
        { wch: 6 }, // S.No
        { wch: 26 }, // User ID
        { wch: 25 }, // Full Name
        { wch: 30 }, // Email Address
        { wch: 15 }, // Phone Number
        { wch: 12 }, // Role
        { wch: 10 }, // Status
        { wch: 30 }, // Ban Reason
        { wch: 15 }, // Email Verified
        { wch: 40 }, // Address
        { wch: 14 }, // Total Orders
        { wch: 16 }, // Completed Orders
        { wch: 15 }, // Pending Orders
        { wch: 18 }, // Cancelled/Refunded
        { wch: 18 }, // Total Amount Spent
        { wch: 20 }, // Account Created
        { wch: 20 }, // Last Updated
      ];
      XLSX.utils.book_append_sheet(workbook, usersSheet, "Users Overview");

      // Sheet 2: All Orders Details
      const ordersData = allOrders.map((order, index) => ({
        "S.No": index + 1,
        "Order ID": order._id,
        "Order Number": order.orderNumber || "N/A",
        "Customer Name": order.user?.name || "N/A",
        "Customer Email": order.user?.email || "N/A",
        "Customer Phone": order.shippingAddress?.phoneNumber || "N/A",
        "Shipping Address": order.shippingAddress
          ? `${order.shippingAddress.address1 || ""}, ${
              order.shippingAddress.city || ""
            }, ${order.shippingAddress.country || ""}`.replace(/^, |, $/g, "")
          : "N/A",
        "Zip Code": order.shippingAddress?.zipCode || "N/A",
        "Items Count": order.cart?.length || 0,
        Products:
          order.cart?.map((item) => `${item.name} (x${item.qty})`).join(", ") ||
          "N/A",
        Subtotal: `$${(order.subTotalPrice || 0).toFixed(2)}`,
        "Shipping Cost": `$${(order.shippingPrice || 0).toFixed(2)}`,
        Discount: `$${(order.discountAmount || 0).toFixed(2)}`,
        "Tax (GST)": `$${(order.gstAmount || 0).toFixed(2)}`,
        "Total Amount": `$${(order.totalPrice || 0).toFixed(2)}`,
        "Payment Method": order.paymentInfo?.type || "N/A",
        "Payment Status": order.paymentInfo?.status || "N/A",
        "Order Status": order.status || "N/A",
        "Coupon Used": order.couponCode || "None",
        "Order Date": order.createdAt
          ? new Date(order.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "N/A",
        "Delivered At": order.deliveredAt
          ? new Date(order.deliveredAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })
          : "N/A",
      }));
      const ordersSheet = XLSX.utils.json_to_sheet(ordersData);
      ordersSheet["!cols"] = [
        { wch: 6 }, // S.No
        { wch: 26 }, // Order ID
        { wch: 15 }, // Order Number
        { wch: 20 }, // Customer Name
        { wch: 28 }, // Customer Email
        { wch: 15 }, // Customer Phone
        { wch: 45 }, // Shipping Address
        { wch: 10 }, // Zip Code
        { wch: 12 }, // Items Count
        { wch: 50 }, // Products
        { wch: 12 }, // Subtotal
        { wch: 14 }, // Shipping Cost
        { wch: 12 }, // Discount
        { wch: 12 }, // Tax (GST)
        { wch: 14 }, // Total Amount
        { wch: 18 }, // Payment Method
        { wch: 15 }, // Payment Status
        { wch: 18 }, // Order Status
        { wch: 15 }, // Coupon Used
        { wch: 25 }, // Order Date
        { wch: 20 }, // Delivered At
      ];
      XLSX.utils.book_append_sheet(workbook, ordersSheet, "All Orders");

      // Sheet 3: Orders by User (Detailed)
      const userOrdersDetailData = [];
      allUsers.forEach((user) => {
        const userOrders = userOrdersMap[user._id] || [];
        if (userOrders.length > 0) {
          userOrders.forEach((order, idx) => {
            userOrdersDetailData.push({
              "User Name": user.name,
              "User Email": user.email,
              "Order #": idx + 1,
              "Order ID": order._id,
              "Order Number": order.orderNumber || "N/A",
              Products:
                order.cart
                  ?.map((item) => `${item.name} (x${item.qty})`)
                  .join(", ") || "N/A",
              "Total Amount": `$${(order.totalPrice || 0).toFixed(2)}`,
              Status: order.status || "N/A",
              Payment: order.paymentInfo?.type || "N/A",
              "Order Date": order.createdAt
                ? new Date(order.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "N/A",
            });
          });
        } else {
          userOrdersDetailData.push({
            "User Name": user.name,
            "User Email": user.email,
            "Order #": "N/A",
            "Order ID": "No Orders",
            "Order Number": "N/A",
            Products: "N/A",
            "Total Amount": "$0.00",
            Status: "N/A",
            Payment: "N/A",
            "Order Date": "N/A",
          });
        }
      });
      const userOrdersSheet = XLSX.utils.json_to_sheet(userOrdersDetailData);
      userOrdersSheet["!cols"] = [
        { wch: 25 }, // User Name
        { wch: 30 }, // User Email
        { wch: 10 }, // Order #
        { wch: 26 }, // Order ID
        { wch: 15 }, // Order Number
        { wch: 50 }, // Products
        { wch: 14 }, // Total Amount
        { wch: 15 }, // Status
        { wch: 15 }, // Payment
        { wch: 18 }, // Order Date
      ];
      XLSX.utils.book_append_sheet(workbook, userOrdersSheet, "Orders by User");

      // Sheet 4: Summary Statistics
      const totalRevenue = allOrders.reduce(
        (sum, o) => sum + (o.totalPrice || 0),
        0
      );
      const deliveredOrders = allOrders.filter((o) => o.status === "Delivered");
      const pendingOrders = allOrders.filter(
        (o) => !["Delivered", "Cancelled", "Refund Success"].includes(o.status)
      );
      const cancelledOrders = allOrders.filter(
        (o) => o.status === "Cancelled" || o.status === "Refund Success"
      );

      const summaryData = [
        { Category: "USER STATISTICS", Metric: "", Value: "" },
        { Category: "", Metric: "Total Users", Value: users.length },
        {
          Category: "",
          Metric: "Active Users",
          Value: users.filter((u) => !u.isBanned).length,
        },
        {
          Category: "",
          Metric: "Banned Users",
          Value: users.filter((u) => u.isBanned).length,
        },
        {
          Category: "",
          Metric: "Admin Users",
          Value: users.filter((u) => u.role === "Admin").length,
        },
        {
          Category: "",
          Metric: "Regular Users",
          Value: users.filter((u) => u.role === "user").length,
        },
        {
          Category: "",
          Metric: "Supplier Users",
          Value: users.filter((u) => u.role === "Supplier").length,
        },
        { Category: "", Metric: "", Value: "" },
        { Category: "ORDER STATISTICS", Metric: "", Value: "" },
        { Category: "", Metric: "Total Orders", Value: allOrders.length },
        {
          Category: "",
          Metric: "Delivered Orders",
          Value: deliveredOrders.length,
        },
        { Category: "", Metric: "Pending Orders", Value: pendingOrders.length },
        {
          Category: "",
          Metric: "Cancelled/Refunded",
          Value: cancelledOrders.length,
        },
        { Category: "", Metric: "", Value: "" },
        { Category: "REVENUE STATISTICS", Metric: "", Value: "" },
        {
          Category: "",
          Metric: "Total Revenue",
          Value: `$${totalRevenue.toFixed(2)}`,
        },
        {
          Category: "",
          Metric: "Avg Order Value",
          Value: `$${
            allOrders.length > 0
              ? (totalRevenue / allOrders.length).toFixed(2)
              : "0.00"
          }`,
        },
        {
          Category: "",
          Metric: "Revenue from Delivered",
          Value: `$${deliveredOrders
            .reduce((sum, o) => sum + (o.totalPrice || 0), 0)
            .toFixed(2)}`,
        },
        { Category: "", Metric: "", Value: "" },
        { Category: "EXPORT INFO", Metric: "", Value: "" },
        {
          Category: "",
          Metric: "Export Date",
          Value: new Date().toLocaleString(),
        },
        { Category: "", Metric: "Exported By", Value: "Positive Moments Admin" },
      ];
      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      summarySheet["!cols"] = [{ wch: 22 }, { wch: 25 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

      // Generate filename with date
      const date = new Date();
      const filename = `Users_Orders_Complete_Export_${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}.xlsx`;

      // Save the file
      XLSX.writeFile(workbook, filename);

      toast.success(
        `Successfully exported ${allUsers.length} users and ${allOrders.length} orders to Excel`
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    await axios
      .delete(`${server}/user/delete-user/${id}`, { withCredentials: true })
      .then((res) => {
        toast.success(res.data.message);
      })
      .catch((error) => {
        toast.error(error.response?.data?.message || "Error deleting user");
      });

    dispatch(getAllUsers(currentPage, 12));
  };

  const handleBanUser = async () => {
    if (!banReason.trim()) {
      toast.error("Please provide a reason for banning");
      return;
    }

    try {
      const response = await axios.put(
        `${server}/user/ban-user/${userId}`,
        { reason: banReason },
        { withCredentials: true }
      );

      toast.success(response.data.message);
      setBanModalAnimating(false);
      setTimeout(() => {
        setBanModalOpen(false);
        setBanReason("");
      }, 300);
      dispatch(getAllUsers(currentPage, 12));
    } catch (error) {
      toast.error(error.response?.data?.message || "Error banning user");
    }
  };

  const handleUnbanUser = async () => {
    try {
      const response = await axios.put(
        `${server}/user/unban-user/${userId}`,
        {},
        { withCredentials: true }
      );

      toast.success(response.data.message);
      setUnbanModalAnimating(false);
      setTimeout(() => setUnbanModalOpen(false), 300);
      dispatch(getAllUsers(currentPage, 12));
    } catch (error) {
      toast.error(error.response?.data?.message || "Error unbanning user");
    }
  };

  const openBanModal = (user) => {
    setSelectedUser(user);
    setUserId(user._id);
    setBanModalOpen(true);
    setTimeout(() => setBanModalAnimating(true), 10);
  };

  const openUnbanModal = (user) => {
    setSelectedUser(user);
    setUserId(user._id);
    setUnbanModalOpen(true);
    setTimeout(() => setUnbanModalAnimating(true), 10);
  };

  const handleCreateUser = async () => {
    const { name, email, password, role, avatar } = newUserData;

    // Validate inputs
    if (!name.trim() || !email.trim() || !password.trim()) {
      toast.error("Please fill all required fields");
      return;
    }

    if (password.length < 4) {
      toast.error("Password should be at least 4 characters");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }

    setCreateUserLoading(true);

    try {
      // Use FormData for file upload
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("email", email.trim());
      formData.append("password", password.trim());
      formData.append("role", role);

      // Add avatar file if provided
      if (avatar) {
        formData.append("file", avatar);
      }

      const response = await axios.post(
        `${server}/user/create-user-by-admin`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(response.data.message);
      setCreateUserModalAnimating(false);

      setTimeout(() => {
        setCreateUserModalOpen(false);
        // Reset form
        setNewUserData({
          name: "",
          email: "",
          password: "",
          role: "user",
          avatar: null,
        });
        setAvatarPreview(null);
      }, 300);

      // Refresh users list
      dispatch(getAllUsers(currentPage, 12));
    } catch (error) {
      console.error("Create user error:", error);
      toast.error(error.response?.data?.message || "Failed to create user");
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await axios.put(
        `${server}/user/change-user-role/${userId}`,
        { role: newRole },
        { withCredentials: true }
      );

      toast.success(response.data.message);
      setRoleAssignModalAnimating(false);
      setTimeout(() => setRoleAssignModalOpen(false), 300);
      dispatch(getAllUsers(currentPage, 12));
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to change user role"
      );
    }
  };

  const openRoleAssignModal = (user) => {
    setSelectedUser(user);
    setUserId(user._id);
    setRoleAssignModalOpen(true);
    setTimeout(() => setRoleAssignModalAnimating(true), 10);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, JPG, PNG, GIF)");
        e.target.value = "";
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        e.target.value = "";
        return;
      }

      setNewUserData((prev) => ({ ...prev, avatar: file }));

      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Filter users based on search term
  const filteredUsers = users?.filter(
    (user) =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  console.log("AllUsers Stats Debug:", {
    totalUsersCount,
    bannedUsersCount,
    adminUsersCount,
    activeUsersCount,
    usersLength: users?.length,
    filteredUsersLength: filteredUsers?.length
  });

  const totalUsers = totalUsersCount || users?.length || 0;
  const bannedUsers = bannedUsersCount || users?.filter((user) => user.isBanned).length || 0;
  const activeUsers = activeUsersCount || (totalUsers - bannedUsers);
  const adminUsers = adminUsersCount || users?.filter((user) => user.role === "Admin").length || 0;

  if (usersLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="w-full p-4 800px:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
            <FiUsers className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl 800px:text-3xl font-bold text-gray-900">
              All Users
            </h1>
            <p className="text-gray-600">
              Manage all registered users and ban system
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 400px:grid-cols-2 800px:grid-cols-4 gap-4 800px:gap-6 mb-6">
        <div className={`${styles.card} ${styles.card_padding}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-primary-600">
                {totalUsers}
              </p>
              <div className="flex items-center mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  All Users
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <FiUsers className="h-6 w-6 text-primary-600" />
            </div>
          </div>
        </div>

        <div className={`${styles.card} ${styles.card_padding}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-2xl font-bold text-green-600">{activeUsers}</p>
              <div className="flex items-center mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <MdCheckCircle className="mr-1" size={12} />
                  Active
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <MdCheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className={`${styles.card} ${styles.card_padding}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Banned Users</p>
              <p className="text-2xl font-bold text-red-600">{bannedUsers}</p>
              <div className="flex items-center mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <BiBlock className="mr-1" size={12} />
                  Banned
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <BiBlock className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className={`${styles.card} ${styles.card_padding}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Admin Users</p>
              <p className="text-2xl font-bold text-purple-600">{adminUsers}</p>
              <div className="flex items-center mt-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Admins
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <MdAdminPanelSettings className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className={`${styles.card} p-4 mb-6`}>
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search users by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.input}
            style={{ paddingLeft: "2.5rem" }}
          />
        </div>
      </div>

      {/* Users Card Grid */}
      <div className={`${styles.card} overflow-hidden p-6`}>
        <div className="flex items-center justify-between pb-6 border-b border-gray-150 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
              <FiUsers className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Users List
              </h2>
              <p className="text-sm text-gray-500">
                {filteredUsers.length} user{filteredUsers.length !== 1 ? "s" : ""} found
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Export to Excel Button */}
            <button
              onClick={exportToExcel}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-all duration-200 shadow-md"
              title="Export all users to Excel"
            >
              <SiMicrosoftexcel size={16} />
              <span>Export to Excel</span>
            </button>

            {/* Create User Button */}
            <button
              onClick={() => {
                setCreateUserModalOpen(true);
                setTimeout(() => setCreateUserModalAnimating(true), 10);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-all duration-200 shadow-md"
            >
              <FiUserPlus size={16} />
              <span>Create User</span>
            </button>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <FiUsers className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your search terms.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((item) => (
              <div
                key={item._id}
                onClick={() => openDetailsModal(item)}
                className="bg-white rounded-xl border border-gray-250 p-5 shadow-sm hover:shadow-md hover:border-primary-300 hover:-translate-y-0.5 transition-all duration-250 cursor-pointer flex items-center space-x-4 group"
              >
                <div className="relative flex-shrink-0">
                  <div className="w-14 h-14 rounded-full overflow-hidden border border-gray-100 group-hover:border-primary-400 transition-colors duration-250">
                    <img
                      src={getAvatarUrl(item.avatar)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><circle cx='12' cy='12' r='12' fill='%23E0E7FF'/><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%236366F1'/></svg>";
                      }}
                    />
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center ${item.isBanned ? 'bg-red-500' : 'bg-green-500'}`}>
                    <div className="w-1.5 h-1.5 rounded-full bg-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-primary-600 transition-colors duration-200">
                      {item.name}
                    </h3>
                  </div>
                  <p className="text-xs text-gray-500 truncate mb-2">{item.email}</p>
                  <div className="flex items-center justify-between">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                      item.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                      item.role === 'Supplier' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {item.role === 'user' ? 'User' : item.role}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      Joined {new Date(item.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-150 pt-6 mt-6 flex-col sm:flex-row gap-4">
            <div className="text-sm text-gray-500">
              Showing page <span className="font-semibold text-gray-800">{currentPage}</span> of{" "}
              <span className="font-semibold text-gray-800">{totalPages}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  currentPage === 1
                    ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, idx) => idx + 1)
                  .filter((page) => {
                    return (
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1
                    );
                  })
                  .map((page, idx, arr) => {
                    const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-all duration-200 ${
                            currentPage === page
                              ? "bg-primary-600 text-white shadow-md"
                              : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  currentPage === totalPages
                    ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {detailsModalOpen && selectedUser && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            detailsModalAnimating ? "bg-black bg-opacity-50" : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden transition-all duration-300 transform ${
              detailsModalAnimating ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            {/* Header Banner */}
            <div className="relative bg-gradient-to-r from-primary-500 to-indigo-600 h-28 flex items-center justify-center px-6">
              <button
                onClick={closeDetailsModal}
                className="absolute top-4 right-4 p-1.5 bg-black bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all duration-200"
              >
                <RxCross1 size={16} />
              </button>
              <div className="absolute -bottom-10 left-6">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-md bg-white">
                  <img
                    src={getAvatarUrl(selectedUser.avatar)}
                    alt={selectedUser.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'><circle cx='12' cy='12' r='12' fill='%23E0E7FF'/><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%236366F1'/></svg>";
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="pt-12 px-6 pb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedUser.name}</h2>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
                <div className="flex flex-col items-end space-y-1">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${
                    selectedUser.role === 'Admin' ? 'bg-purple-100 text-purple-700' :
                    selectedUser.role === 'Supplier' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedUser.role === 'user' ? 'User' : selectedUser.role}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center space-x-1 ${
                    selectedUser.isBanned ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {selectedUser.isBanned ? 'Banned' : 'Active'}
                  </span>
                </div>
              </div>

              {/* User Metadata */}
              <div className="border-t border-gray-100 pt-4 mb-4">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">User Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="block text-xs text-gray-400">Phone Number</span>
                    <span className="text-gray-800 font-medium">{selectedUser.phoneNumber || "Not provided"}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-gray-400">Joined Date</span>
                    <span className="text-gray-800 font-medium">
                      {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString("en-US", {
                        year: 'numeric', month: 'long', day: 'numeric'
                      }) : "N/A"}
                    </span>
                  </div>
                </div>
                {selectedUser.isBanned && selectedUser.banReason && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                    <span className="block text-xs text-red-800 font-bold mb-1">Ban Reason</span>
                    <p className="text-sm text-red-700">{selectedUser.banReason}</p>
                  </div>
                )}
              </div>

              {/* Addresses Section */}
              <div className="border-t border-gray-100 pt-4 mb-6">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Saved Addresses ({selectedUser.addresses?.length || 0})</h4>
                {selectedUser.addresses && selectedUser.addresses.length > 0 ? (
                  <div className="space-y-2 max-h-[140px] overflow-y-auto">
                    {selectedUser.addresses.map((addr, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 border border-gray-150 rounded-lg text-xs">
                        <div className="flex justify-between font-bold text-gray-700 mb-1">
                          <span className="capitalize">{addr.addressType} Address</span>
                          {addr.isDefault && <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">Default</span>}
                        </div>
                        <p className="text-gray-600">
                          {addr.address1}, {addr.address2 && `${addr.address2}, `}{addr.city}, {addr.state && `${addr.state}, `}{addr.country} - {addr.zipCode}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No saved addresses</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="border-t border-gray-100 pt-4 flex space-x-3 justify-end">
                {selectedUser.role !== "Admin" && (
                  <button
                    onClick={() => {
                      closeDetailsModal();
                      setTimeout(() => openRoleAssignModal(selectedUser), 350);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    Change Role
                  </button>
                )}
                {selectedUser.role !== "Admin" && (
                  selectedUser.isBanned ? (
                    <button
                      onClick={() => {
                        closeDetailsModal();
                        setTimeout(() => openUnbanModal(selectedUser), 350);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      Unban User
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        closeDetailsModal();
                        setTimeout(() => openBanModal(selectedUser), 350);
                      }}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
                    >
                      Ban User
                    </button>
                  )
                )}
                <button
                  onClick={() => {
                    closeDetailsModal();
                    setUserId(selectedUser._id);
                    setTimeout(() => setOpen(true), 350);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-unacademy-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <RxCross1 size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiTrash2 className="text-red-600 h-6 w-6" />
              </div>
              <p className="text-gray-600 text-center">
                Are you sure you want to delete this user? This action cannot be
                undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setOpen(false)}
                className={`flex-1 ${styles.button_outline}`}
              >
                Cancel
              </button>
              <button
                onClick={() => setOpen(false) || handleDelete(userId)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-all duration-200 shadow-unacademy hover:shadow-unacademy-md"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban User Modal */}
      {banModalOpen && selectedUser && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            banModalAnimating
              ? "bg-black bg-opacity-50"
              : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-lg shadow-unacademy-xl p-6 w-full max-w-md mx-4 transition-all duration-300 transform ${
              banModalAnimating
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Ban User</h3>
              <button
                onClick={() => {
                  setBanModalAnimating(false);
                  setTimeout(() => setBanModalOpen(false), 300);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <RxCross1 size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiShieldOff className="text-orange-600 h-6 w-6" />
              </div>
              <p className="text-gray-600 text-center mb-4">
                You are about to ban <strong>{selectedUser.name}</strong>.
                Please provide a reason for this action.
              </p>

              <textarea
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="Enter reason for banning this user..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                rows={4}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {banReason.length}/500 characters
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setBanModalAnimating(false);
                  setTimeout(() => setBanModalOpen(false), 300);
                }}
                className={`flex-1 ${styles.button_outline}`}
              >
                Cancel
              </button>
              <button
                onClick={handleBanUser}
                disabled={!banReason.trim()}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-all duration-200 shadow-unacademy hover:shadow-unacademy-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ban User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unban User Modal */}
      {unbanModalOpen && selectedUser && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            unbanModalAnimating
              ? "bg-black bg-opacity-50"
              : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-lg shadow-unacademy-xl p-6 w-full max-w-md mx-4 transition-all duration-300 transform ${
              unbanModalAnimating
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Unban User
              </h3>
              <button
                onClick={() => {
                  setUnbanModalAnimating(false);
                  setTimeout(() => setUnbanModalOpen(false), 300);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <RxCross1 size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiShield className="text-green-600 h-6 w-6" />
              </div>
              <p className="text-gray-600 text-center">
                Are you sure you want to unban{" "}
                <strong>{selectedUser.name}</strong>? They will regain full
                access to their account.
              </p>
              {selectedUser.banReason && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-800">
                    <strong>Current ban reason:</strong>{" "}
                    {selectedUser.banReason}
                  </p>
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setUnbanModalAnimating(false);
                  setTimeout(() => setUnbanModalOpen(false), 300);
                }}
                className={`flex-1 ${styles.button_outline}`}
              >
                Cancel
              </button>
              <button
                onClick={handleUnbanUser}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-all duration-200 shadow-unacademy hover:shadow-unacademy-md"
              >
                Unban User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {createUserModalOpen && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            createUserModalAnimating
              ? "bg-black bg-opacity-50"
              : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-lg shadow-unacademy-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto transition-all duration-300 transform ${
              createUserModalAnimating
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Create New User
              </h3>
              <button
                onClick={() => {
                  setCreateUserModalAnimating(false);
                  setTimeout(() => {
                    setCreateUserModalOpen(false);
                    setNewUserData({
                      name: "",
                      email: "",
                      password: "",
                      role: "user",
                      avatar: null,
                    });
                    setAvatarPreview(null);
                  }, 300);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
                disabled={createUserLoading}
              >
                <RxCross1 size={20} className="text-gray-500" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateUser();
              }}
            >
              {/* Avatar Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Picture (Optional)
                </label>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt="Avatar Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <AiOutlineUser className="text-2xl text-gray-400" />
                    )}
                  </div>
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                      id="avatar-upload"
                      disabled={createUserLoading}
                    />
                    <label
                      htmlFor="avatar-upload"
                      className="cursor-pointer inline-flex items-center space-x-2 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                      <AiOutlineUpload size={14} />
                      <span className="text-sm">Upload Image</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Max 5MB, JPEG/PNG/GIF
                    </p>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={newUserData.name}
                  onChange={(e) =>
                    setNewUserData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className={styles.input}
                  placeholder="Enter full name"
                  disabled={createUserLoading}
                  required
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) =>
                    setNewUserData((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  className={styles.input}
                  placeholder="Enter email address"
                  disabled={createUserLoading}
                  required
                />
              </div>

              {/* Password */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) =>
                    setNewUserData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  className={styles.input}
                  placeholder="Enter password (min 4 characters)"
                  disabled={createUserLoading}
                  required
                  minLength={4}
                />
              </div>

              {/* Role */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Role *
                </label>
                <select
                  value={newUserData.role}
                  onChange={(e) =>
                    setNewUserData((prev) => ({
                      ...prev,
                      role: e.target.value,
                    }))
                  }
                  className={styles.input}
                  disabled={createUserLoading}
                  required
                >
                  <option value="user">User</option>
                  <option value="Supplier">Supplier</option>
                  <option value="Admin">Admin</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose the appropriate role for this user
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setCreateUserModalOpen(false);
                    setNewUserData({
                      name: "",
                      email: "",
                      password: "",
                      role: "user",
                      avatar: null,
                    });
                    setAvatarPreview(null);
                  }}
                  className={`flex-1 ${styles.button_outline}`}
                  disabled={createUserLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    createUserLoading ||
                    !newUserData.name.trim() ||
                    !newUserData.email.trim() ||
                    !newUserData.password.trim()
                  }
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-all duration-200 shadow-unacademy hover:shadow-unacademy-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {createUserLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <FiUserPlus size={16} />
                      <span>Create User</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Role Assignment Modal */}
      {roleAssignModalOpen && selectedUser && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
            roleAssignModalAnimating
              ? "bg-black bg-opacity-50"
              : "bg-black bg-opacity-0"
          }`}
        >
          <div
            className={`bg-white rounded-lg shadow-unacademy-xl p-6 w-full max-w-md mx-4 transition-all duration-300 transform ${
              roleAssignModalAnimating
                ? "opacity-100 scale-100 translate-y-0"
                : "opacity-0 scale-95 translate-y-4"
            }`}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Change User Role
              </h3>
              <button
                onClick={() => {
                  setRoleAssignModalAnimating(false);
                  setTimeout(() => setRoleAssignModalOpen(false), 300);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors duration-200"
              >
                <RxCross1 size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FiSettings className="text-blue-600 h-6 w-6" />
              </div>
              <p className="text-gray-600 text-center mb-4">
                Change role for <strong>{selectedUser.name}</strong>
              </p>
              <p className="text-sm text-gray-500 text-center mb-4">
                Current role:{" "}
                <span className="font-medium text-gray-700">
                  {selectedUser.role}
                </span>
              </p>

              <div className="space-y-3">
                {["User", "Supplier", "Admin"].map((role) => (
                  <button
                    key={role}
                    onClick={() => handleRoleChange(selectedUser._id, role)}
                    disabled={role === selectedUser.role}
                    className={`w-full p-3 rounded-lg border transition-all duration-200 text-left ${
                      role === selectedUser.role
                        ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-white border-gray-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">{role}</div>
                        <div className="text-sm text-gray-500">
                          {role === "User" &&
                            "Regular user with basic permissions"}
                          {role === "Supplier" &&
                            "Can create and manage products"}
                          {role === "Admin" &&
                            "Full system administrator access"}
                        </div>
                      </div>
                      {role === selectedUser.role && (
                        <MdCheckCircle className="text-green-500" size={20} />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setRoleAssignModalOpen(false)}
                className={`flex-1 ${styles.button_outline}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllUsers;
